import { isAxiosError } from "helpers/utils.helper"
import { sanitizeFileNamePart, stripPdfExtension } from "helpers/fileName.helper"
import { DETAILS_WORD_MESSAGES, DETAILS_WORD_NAME_PART_MAX_LENGTH } from "helpers/detailsWord.constants"

const sanitizeNamePart = (value: string) => sanitizeFileNamePart(value, DETAILS_WORD_NAME_PART_MAX_LENGTH)

// Thrown when the build endpoint itself reports `generation_status: "failed"` (or resolves without a
// usable `download_url`) — distinct from an axios/network error, so `getDetailsWordErrorMessage` can
// tell "the request failed" apart from "the request succeeded, but the build it started did not."
export class DetailsWordGenerationFailedError extends Error {}

// Thrown when polling exceeds DETAILS_WORD_POLL_TIMEOUT_MS without the job reaching a terminal state.
// The CSV flow has no equivalent: it is a single synchronous call with its own axios timeout instead.
export class DetailsWordPollTimeoutError extends Error {}

// Composes the download name for the on-demand Quotation Details Word file (#1448):
// `<original PDF>__quotation_<quotation name>.docx`, matching the CSV convention (REQ-420) and the
// annotated result PDF's own naming (#1401).
//
// Includes the `.docx` extension, matching `buildResultCsvFileName` / `buildResultPdfFileName` —
// `downloadFileService.downloadFromUrl` (the presigned-URL download path this flow uses) takes the
// whole file name as-is, extension included, same as `downloadBlob`.
//
// `quotationName` is required, with no unnamed case to handle: the API never returns a null name —
// `Quotations.to_dict()` substitutes "Untitled" — so a quotation always has one from here.
//
// `drawingName` is genuinely optional: its query can still be in flight on the first paint after the
// drawer opens, or have failed.
export const buildDetailsWordFileName = ({
	quotationName,
	drawingName,
}: {
	quotationName: string
	drawingName?: string | null
}): string => {
	const drawingPart = drawingName ? sanitizeNamePart(stripPdfExtension(drawingName)) : ""
	const quotationPart = sanitizeNamePart(quotationName)

	return drawingPart ? `${drawingPart}__quotation_${quotationPart}.docx` : `quotation_${quotationPart}.docx`
}

// Maps a failure of the on-demand Word-file flow to short, actionable copy. Three source shapes,
// checked in order: our own poll timeout, the build endpoint's own terminal "failed" status, then an
// axios error from either the start or the poll request (401/403/404/409 + client timeout, same
// mapping as the CSV flow's `getResultCsvErrorMessage` and the PDF flow's `getResultPdfErrorMessage`).
export const getDetailsWordErrorMessage = (error: unknown): string => {
	if (error instanceof DetailsWordPollTimeoutError) return DETAILS_WORD_MESSAGES.timedOut
	if (error instanceof DetailsWordGenerationFailedError) return DETAILS_WORD_MESSAGES.failed
	if (!isAxiosError(error)) return DETAILS_WORD_MESSAGES.failed
	// Axios reports both its own client timeout and a dropped connection as ECONNABORTED.
	if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") return DETAILS_WORD_MESSAGES.timedOut

	switch (error.response?.status) {
		// There is no global 401 interceptor, so this has to say "signed out" itself — otherwise an
		// expired token reads as a generation failure and invites an endless retry.
		case 401:
			return DETAILS_WORD_MESSAGES.unauthorized
		case 403:
			return DETAILS_WORD_MESSAGES.forbidden
		case 404:
			return DETAILS_WORD_MESSAGES.notFound
		case 409:
			return DETAILS_WORD_MESSAGES.submitted
		default:
			return DETAILS_WORD_MESSAGES.failed
	}
}
